/**
 * Build-time environment variable validation.
 *
 * Vite bakes `VITE_*` env vars into the bundle at build time as literal
 * string substitutions. If you forget to set one when building, the
 * resulting bundle silently has an empty string in its place — at runtime,
 * `fetch('${VITE_PAYMENTS_URL}/foo')` becomes `fetch('/foo')`, hits the
 * SPA's own host, returns index.html (the SPA fallback), and breaks with
 * "Unexpected token '<'" when the frontend tries to JSON.parse the HTML.
 *
 * The `npm run build:prod` script eliminates that mistake at build time
 * by sourcing values from CloudFormation. This module is the runtime
 * belt-and-suspenders: if a bad bundle ever DOES ship, the page renders
 * a clear error overlay instead of cryptic JSON parse failures
 * sprinkled through the app.
 */

const REQUIRED_ENV_VARS = [
  'VITE_PAYMENTS_URL',
  'VITE_MCP_URL',
  'VITE_AUTH_URL',
  'VITE_FRONTEND_CLIENT_ID',
  'VITE_MCP_SCOPE',
] as const;

/** Returns the names of any required VITE_* vars that weren't set at build. */
function findMissingEnvVars(): string[] {
  return REQUIRED_ENV_VARS.filter((name) => {
    const value = import.meta.env[name];
    return value === undefined || value === null || value === '';
  });
}

/** Renders a full-page error overlay listing the missing vars + the fix. */
function renderEnvErrorOverlay(missing: string[]): void {
  const root = document.getElementById('root') ?? document.body;
  root.innerHTML = `
    <div style="
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem;
      background: #0f172a;
      color: #f8fafc;
      font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
    ">
      <div style="max-width: 640px;">
        <h1 style="font-size: 1.5rem; font-weight: 700; margin-bottom: 1rem; color: #f59e0b;">
          ⚠️ Frontend build is missing configuration
        </h1>
        <p style="margin-bottom: 1rem; line-height: 1.6;">
          This bundle was built without the following required environment variables:
        </p>
        <ul style="
          background: #1e293b;
          border-radius: 0.5rem;
          padding: 1rem 1.5rem;
          margin-bottom: 1rem;
          font-family: ui-monospace, SFMono-Regular, monospace;
          color: #fca5a5;
        ">
          ${missing.map((name) => `<li>${name}</li>`).join('')}
        </ul>
        <p style="margin-bottom: 1rem; line-height: 1.6;">
          To fix: rebuild the frontend with
          <code style="background:#1e293b; padding:0.1rem 0.4rem; border-radius:0.25rem;">npm run build:prod</code>
          (which sources these from CloudFormation), then redeploy
          <code style="background:#1e293b; padding:0.1rem 0.4rem; border-radius:0.25rem;">SleeperFrontendStack</code>.
        </p>
        <p style="font-size: 0.875rem; color: #94a3b8;">
          (If you're a customer seeing this page, please email
          <a style="color: #f59e0b;" href="mailto:support@thedraftlion.com">support@thedraftlion.com</a> —
          our deploy is misconfigured.)
        </p>
      </div>
    </div>
  `;
}

/**
 * Call once at app boot. In dev, missing vars warn to console (you may be
 * intentionally running without one). In prod, missing vars halt the app
 * with a visible error overlay.
 */
export function assertEnv(): void {
  const missing = findMissingEnvVars();
  if (missing.length === 0) return;

  if (import.meta.env.PROD) {
    renderEnvErrorOverlay(missing);
    throw new Error(
      `Frontend bundle missing required env vars: ${missing.join(', ')}. ` +
      `Run 'npm run build:prod' before 'cdk deploy SleeperFrontendStack'.`,
    );
  } else {
    // Dev: noisy console warning but don't break the app, since you may
    // be running `npm run dev` to work on a feature that doesn't touch
    // these endpoints.
    console.warn(
      '[draftlion] Missing VITE_* env vars (dev mode, not fatal):',
      missing,
    );
  }
}
